import PiAlertTriangleSolid from "@markeroo/icons/src/solid/alerts/PiAlertTriangleSolid";

import { useGCStatus } from "@/api/hooks";
import { useFeatureFlag } from "@/providers/FeatureFlagProvider";
import { useAuthStore } from "@/stores/auth";
import { useShallow } from "zustand/shallow";
import { API_ORIGIN } from "@/api/config";
import { ui } from "@/strings";

/**
 * Banner that appears when the user's Google Classroom connection has expired.
 *
 * This shows when:
 * - User has gc_mode enabled (they previously connected)
 * - But the refresh token is missing (backend cleared it on 401)
 *
 * The banner pushes down the rest of the page and provides a prominent
 * reconnect button to restore the connection.
 */
export function GCReconnectBanner() {
  const gcIntegrationEnabled = useFeatureFlag("gc_integration");
  const { user } = useAuthStore(useShallow((state) => ({ user: state.user })));
  const { data: gcStatus, isLoading } = useGCStatus({
    enabled: gcIntegrationEnabled && !!user,
  });

  // Don't show if:
  // - Feature flag is off
  // - User is not logged in
  // - Still loading GC status
  // - GC mode is not enabled (user never connected)
  // - Refresh token is present (connection is healthy)
  if (
    !gcIntegrationEnabled ||
    !user ||
    isLoading ||
    !gcStatus?.gc_mode ||
    gcStatus?.has_refresh_token
  ) {
    return null;
  }

  const handleReconnect = () => {
    // Redirect to Google Classroom OAuth flow, returning to current page
    const nextPath = window.location.pathname;
    window.location.href = `${API_ORIGIN}/api/google-classroom/connect?next=${encodeURIComponent(nextPath)}`;
  };

  return (
    <div className="bg-semantic-error text-white px-4 py-4">
      <div className="mx-auto max-w-7xl flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <PiAlertTriangleSolid size={24} className="flex-shrink-0" />
          <div>
            <p className="font-semibold">
              {ui.googleClassroom.connectionExpiredTitle}
            </p>
            <p className="text-sm text-semantic-error-light">
              {ui.googleClassroom.connectionExpiredDescription}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={handleReconnect}
          className="rounded-md bg-white px-4 py-2 text-sm font-medium text-semantic-error transition hover:bg-semantic-error-light flex-shrink-0"
        >
          {ui.googleClassroom.reconnect}
        </button>
      </div>
    </div>
  );
}
